import React, { useRef } from 'react';
import { Link, useLoaderData } from '@remix-run/react';
import type { loader as homeLoader } from '~/routes/_app._index';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';
import {
    formatNumberWithCommas,
    roundToNearest,
} from '@rocco/utils/format-number';
import { FilterTabs } from '@rocco/ui/filter-sort/components/filters-bar/FilterTabs';
import { Grid } from '@rocco/ui/layout/components/Grid';
import { Tile } from '@rocco/ui/tile-shared/components/Tile';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import { Button as RoccoButton } from '@rocco/components/button';
import { useLogImpression } from '@archipro-website/tracker';
import { useCTAHandlers } from '~/ui-rocco/hooks/use-home-cta-actions';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { TileOverlayWithFollowSelector } from '~/modules/moodboards/components';

export const HomePageProjects = () => {
    const { projectSection, appDisplayMode } =
        useLoaderData<typeof homeLoader>();

    const { handleCTAClick, handleCategoryTabClick } =
        useCTAHandlers('HomePageProjects');

    return (
        <section className="py-20 md:py-[10.25rem] qhd:mx-auto qhd:max-w-[1920px]">
            <div className="flex flex-col items-center justify-center gap-6 px-6 pb-6 md:gap-12 md:px-0 md:pb-[6.375rem]">
                <h3 className="text-center text-3xl leading-[127.78%] tracking-[-0.125rem] md:flex md:gap-10.5 md:text-20 md:leading-[100%]">
                    <span>{`${projectSection.prefixTitle} `}</span>
                    <span className="mx-2 inline-block rounded-full border-2 border-black px-4 py-0.5 leading-[100%] md:mx-0 md:px-7.5">{`${formatNumberWithCommas(roundToNearest(projectSection.count))}+`}</span>
                    <span>{`${projectSection.suffixTitle}`}</span>
                </h3>
                <p className="py-1.5 text-center text-base text-gray-700 md:max-w-[46.875rem] md:text-5 md:leading-[130%] md:tracking-[0.025rem]">
                    {projectSection.description}
                </p>
            </div>
            <div className="pt-4.5 md:px-12">
                <div className="flex flex-col gap-6.5 md:gap-17">
                    <div className="pl-6 md:mt-0 md:block md:pl-0">
                        <div className="scrollbar-hide flex w-full items-center overflow-x-auto">
                            <FilterTabs
                                tabs={projectSection.filterTabs}
                                displayMode={appDisplayMode}
                                onTabClick={() => {
                                    handleCategoryTabClick?.(
                                        'HomePageProjects',
                                    );
                                }}
                                preventScrollReset={false}
                            />
                        </div>
                    </div>
                    <div className="px-6 md:px-0">
                        <Grid
                            colClasses="grid-cols-1 md:grid-cols-2 md:grid-cols-4"
                            rowsGapClass="gap-y-12 md:gap-y-26"
                        >
                            {projectSection.tiles.map((tile) => (
                                <HomePageProjectsTile
                                    key={`featured-project-tile-${tile.model.id}`}
                                    tile={
                                        tile as TileViewModel<ProjectTileViewModel>
                                    }
                                />
                            ))}
                        </Grid>
                    </div>
                </div>
            </div>
            <div className="mt-12 text-center md:mt-30">
                <RoccoButton color="primary" asChild>
                    <Link
                        to={projectSection.CTAButton?.link ?? '/projects'}
                        onClick={() => handleCTAClick()}
                    >
                        {projectSection.CTAButton?.text}
                        <ArrowRight className="ml-1.5 w-4" />
                    </Link>
                </RoccoButton>
            </div>
        </section>
    );
};

const HomePageProjectsTile = ({
    tile,
}: {
    tile: TileViewModel<ProjectTileViewModel>;
}) => {
    const tileRef = useRef<HTMLDivElement>(null);
    const { isInDesignBoard } = useHandleFollow();

    useLogImpression({
        data: {
            type: 'LibraryItem_Directory',
            itemID: tile.model.id,
            professionalID: tile.model.professional.id,
        },
        ref: tileRef,
    });

    return (
        <div ref={tileRef}>
            <Tile
                tile={{
                    ...tile,
                    isFollowed: isInDesignBoard(tile.model.id, 'ProjectPin'),
                    overlay: (
                        <TileOverlayWithFollowSelector
                            pinId={tile.model.id}
                            pinType="ProjectPin"
                        />
                    ),
                }}
            />
        </div>
    );
};
